import { SyntheticEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// @ts-ignore // eslint-disable-next-line
import useMobileDetect from 'use-mobile-detect-hook';

import HandRaiserToast from 'components/ChatBubble/HandRaiserToast/HandRaiserToast';
import DynamicHandRaiserToast from './DynamicHandRaiserToast';

import { useChatContext } from 'hooks/chatContext';
import { useConversation } from 'hooks/conversation';
import { useUrlListener } from 'hooks/useUrlListener';
import { useView } from 'hooks/view';

import { getMojoContext } from 'utils/chatContext';
import {
  shouldTriggerHandRaiser,
  updateHandRaiserStorage,
  getHandRaiserConfig,
} from 'utils/handRaiser';
import { config } from 'utils/handRaiser/config';

import {
  DynamicHandraiserNameType,
  DynamicHandRaiserResponseType,
} from 'services/chatBackend/types/handraiser';
import { Experiment, When } from '@carvana/core-experiment';
import { EXPERIMENT_HANDRAISER_STC } from 'constants/experiments';
import { CHATUI_WINDOW_PATH } from 'constants/global';
import { ACTIONS } from 'constants/hook';
import { URL_PATHNAME } from 'constants/url';
import { ROUTES } from 'routes/constants';
import { DataDogLogger } from 'utils/dataDog';
import { CUSTOM_ACTION_NAMES } from 'utils/dataDog/constants';
import { getIsInAssignmentQueue } from 'utils/message/message';

const HandRaiserToastManager = () => {
  const { url } = useUrlListener(true);
  const urlObj = new URL(url);
  const pathname = urlObj.pathname;
  const isPurchasePage = pathname.includes(URL_PATHNAME.PURCHASE);
  const isSTCPage = pathname.includes(URL_PATHNAME.STC);
  const isSearchPage = pathname.includes(URL_PATHNAME.SRP);

  // early return for pages without handraiser
  if (!isPurchasePage && !isSTCPage && !isSearchPage) {
    return null;
  }

  const { dispatchChatContextAction, chatContextState } = useChatContext();
  const { conversationState, dispatchConversationAction } = useConversation();
  const { viewState } = useView();
  const navigate = useNavigate();
  const detectMobile = useMobileDetect();
  const isMobile = detectMobile.isMobile();

  const { matchedConfigKey, matchedConfig: currentHandraiserConfig } =
    getHandRaiserConfig(pathname);

  // Used to keep track of the the timeout used to delay showing the toast to the user
  const toastTimeoutId = useRef<NodeJS.Timeout>();

  const isHandRaiserAvailableFn = () => {
    const isInQueue = getIsInAssignmentQueue(conversationState.messages);
    const isHandRaiserCondition = shouldTriggerHandRaiser(pathname, currentHandraiserConfig);
    const isNoAdvocates = window[CHATUI_WINDOW_PATH].getAdvocates()?.length === 0;

    return !viewState.isPanelOpen && isNoAdvocates && isHandRaiserCondition && !isInQueue;
  };

  // Determines the current visibility state of the HR toast
  const [isHandRaiserVisible, setIsHandRaiserVisible] = useState(false);

  const messageIntent = currentHandraiserConfig?.messageIntent;

  const showToastWithDelay = () => {
    // No need to create a timeout if toast is not available
    if (!isHandRaiserAvailableFn()) return;

    // Different delay times on some mobile/desktop pages, otherwise default delay
    const configDelay = isMobile
      ? currentHandraiserConfig?.displayDelayMobile
      : currentHandraiserConfig?.displayDelayWeb;
    const delay = configDelay || config.defaultDisplayDelay;

    toastTimeoutId.current = setTimeout(() => {
      setIsHandRaiserVisible(true);
    }, delay);
  };

  /**
   * Clears out the current toast timeout to prepare for a new toast to be shown.
   */
  const clearPreviousToast = () => {
    if (toastTimeoutId.current) {
      clearTimeout(toastTimeoutId.current);
      setIsHandRaiserVisible(false);
    }
  };

  const setHandRaiser = () => {
    clearPreviousToast();
    showToastWithDelay();
  };

  useEffect(() => {
    setHandRaiser();
  }, [pathname]);

  // Watch for chat open
  // Do not show HR if chat is already open
  useEffect(() => {
    setHandRaiser();
  }, [viewState.isPanelOpen]);

  useEffect(() => {
    setHandRaiser();
    return () => {
      clearTimeout(toastTimeoutId.current);
    };
  }, []);

  const handleCloseToast = (e: SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsHandRaiserVisible(false);
  };

  const handleStartChat = async (
    msgIntent: string | undefined,
    dynamicHandraiserData?: DynamicHandRaiserResponseType,
  ) => {
    setIsHandRaiserVisible(false);
    if (msgIntent) {
      let newContext;
      const isEngineContextUpdateNeeded = Boolean(dynamicHandraiserData);
      if (isEngineContextUpdateNeeded && dynamicHandraiserData) {
        // update Engine context so Sebastian responds correctly going forward.
        newContext = getMojoContext();
        newContext.info.handraiser = {
          handraiser_context: JSON.stringify(dynamicHandraiserData.handraiserContext),
          handraiser_id: dynamicHandraiserData.handraiserId,
          handraiser_name: dynamicHandraiserData.handraiserName,
        };
      }
      if (conversationState.isConnected) {
        isEngineContextUpdateNeeded && (await window[CHATUI_WINDOW_PATH].putContext(newContext));
        await window[CHATUI_WINDOW_PATH].sendMessage(msgIntent, null, false);
      } else {
        isEngineContextUpdateNeeded &&
          dispatchChatContextAction({
            type: ACTIONS.CHANGE,
            payload: {
              ...chatContextState,
              mojoContext: newContext,
              isInitWithDynamicHandRaiser: true,
            },
          });
        dispatchConversationAction({
          type: ACTIONS.CHANGE,
          payload: {
            isConnected: false,
            isInitializationRequested: true,
            questionInputText: msgIntent,
            questionWhenInitializing: msgIntent,
          },
        });
      }
      DataDogLogger.action(CUSTOM_ACTION_NAMES.HANDRAISER_MESSAGE_INTENT, {
        pathname,
        messageIntent,
        experimentName: currentHandraiserConfig?.experiment,
        handraiserName: currentHandraiserConfig?.handraiserName,
      });
      window[CHATUI_WINDOW_PATH].show();
      navigate(ROUTES.CHAT);
    }
  };

  const handleUpdateHandRaiserShown = () =>
    matchedConfigKey &&
    updateHandRaiserStorage(matchedConfigKey, currentHandraiserConfig?.elapsedExpiry);

  const commonHandraiserProps = {
    handleCloseToast,
    handleUpdateHandRaiserShown,
    handraiserName: currentHandraiserConfig?.handraiserName as DynamicHandraiserNameType,
  };

  const dynamicHandraiserProps = {
    ...commonHandraiserProps,
    handleStartChat,
    currentExperiment: {
      name: currentHandraiserConfig?.experiment as string,
      bucket: currentHandraiserConfig?.experimentBucket as string,
    },
  };

  const staticHandRaiserProps = {
    ...commonHandraiserProps,
    handleStartChat: () => handleStartChat(messageIntent),
    displayMessage: currentHandraiserConfig?.displayMessage,
    openChatCTA: 'Chat Now',
    experimentName: currentHandraiserConfig?.experiment,
    isPurchasePage,
  };

  if (isPurchasePage) {
    const isHRPurchaseStep = isHandRaiserVisible && pathname === matchedConfigKey;
    return isHRPurchaseStep ? <HandRaiserToast {...staticHandRaiserProps} /> : null;
  }

  if (isSearchPage) {
    return isHandRaiserVisible ? <DynamicHandRaiserToast {...dynamicHandraiserProps} /> : null;
  }

  if (isSTCPage) {
    return (
      <Experiment name={EXPERIMENT_HANDRAISER_STC.name}>
        {isHandRaiserVisible && currentHandraiserConfig?.experimentBucket && (
          <When
            bucket={currentHandraiserConfig.experimentBucket}
            experimentName={EXPERIMENT_HANDRAISER_STC.name}
          >
            <HandRaiserToast {...staticHandRaiserProps} />
          </When>
        )}
      </Experiment>
    );
  }

  return null;
};

export default HandRaiserToastManager;
